import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, User, QrCode, LogOut, LayoutDashboard, MessageCircle, Building2, Sparkles, Heart, Wand2, MapPin, Wallet, Gift } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import AuthModal from './AuthModal';
import WebsiteQRModal from './WebsiteQRModal';
import LanguageSwitcher from './LanguageSwitcher';
import AIChatAssistant from './AIChatAssistant';
import { FEATURE_FLAGS } from '../config';
import RoleSwitcher from '../shared/common/RoleSwitcher';
import NotificationBell from './NotificationBell';
import { useBrand } from '../brand/useBrand';
import { UCWonderLogo } from './UCWonderLogo';
import { UCWonderNav } from './UCWonderNav';
import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LegacyHeader = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Brand-aware copy + cross-brand link generation. Falls back to the
  // stays defaults when no provider is mounted (e.g. design previews).
  const { brand, brandKey, sibling, crossBrandHref } = useBrand();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [showQRModal, setShowQRModal] = useState(false);
  const [showAIChat, setShowAIChat] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [userRoles, setUserRoles] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [walletCount, setWalletCount] = useState(0);

  // Fetch the customer's voucher count so the header wallet icon can
  // show a badge. Only fires when logged in. Failures are silent —
  // a missing badge should never block the rest of the header rendering.
  useEffect(() => {
    if (!isLoggedIn) {
      setWalletCount(0);
      return;
    }
    const token = localStorage.getItem('user_token');
    if (!token) return;
    let cancelled = false;
    axios
      .get(`${API_BASE}/wallet/vouchers`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (cancelled) return;
        const items = r.data?.items || r.data?.vouchers || [];
        // Count only redeemable (active / unredeemed) vouchers.
        const active = items.filter((v) =>
          ['active', 'unredeemed', 'fulfilled', 'ready'].includes((v.status || '').toLowerCase())
        ).length;
        setWalletCount(active || items.length || 0);
      })
      .catch(() => { /* silent — no badge */ });
    return () => { cancelled = true; };
  }, [isLoggedIn]);

  // Separate useEffect for polling unread messages
  useEffect(() => {
    const token = localStorage.getItem('user_token');
    if (!token) return;

    // Fetch immediately
    fetchUnreadCount(token);
    
    // Poll every 30 seconds
    const interval = setInterval(() => {
      const currentToken = localStorage.getItem('user_token');
      if (currentToken) {
        fetchUnreadCount(currentToken);
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [isLoggedIn]);

  useEffect(() => {
    // Check login status whenever component renders or route changes
    checkLoginStatus();
  }, []);

  // Also check on storage changes (when user logs in/out in another tab or component)
  useEffect(() => {
    const handleStorageChange = () => {
      checkLoginStatus();
    };
    
    window.addEventListener('storage', handleStorageChange);
    // Also listen for custom event when login state changes
    window.addEventListener('loginStateChanged', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('loginStateChanged', handleStorageChange);
    };
  }, []);

  const checkLoginStatus = () => {
    // Check both user_token (for customers) and admin_token (for admins)
    const userToken = localStorage.getItem('user_token');
    const adminToken = localStorage.getItem('admin_token');
    
    // Determine which account to use based on current route
    const isAdminRoute = window.location.pathname.startsWith('/admin');
    
    let token, userData;
    
    if (isAdminRoute && adminToken) {
      // On admin routes, prioritize admin account
      token = adminToken;
      const adminData = localStorage.getItem('admin_data');
      userData = adminData;
    } else if (userToken) {
      // On non-admin routes, use regular user account
      token = userToken;
      userData = localStorage.getItem('user_data');
    } else if (adminToken) {
      // Fallback to admin if no user token but admin token exists
      token = adminToken;
      const adminData = localStorage.getItem('admin_data');
      userData = adminData;
    }
    
    if (token && userData) {
      try {
        const data = JSON.parse(userData);
        setIsLoggedIn(true);
        setCustomerName(data.full_name || data.username || data.email || 'User');
        setUserRoles(data.roles || ['customer']);
      } catch {
        setIsLoggedIn(false);
        setCustomerName('');
        setUserRoles([]);
      }
    } else {
      setIsLoggedIn(false);
      setCustomerName('');
      setUserRoles([]);
    }
  };

  const fetchUnreadCount = async (token) => {
    try {
      const response = await axios.get(`${API_BASE}/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const handleAuthClick = (mode) => {
    setAuthMode(mode);
    setIsAuthModalOpen(true);
  };

  const handleLogout = () => {
    // Clear httpOnly session cookies server-side (fire-and-forget)
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
    // Clear both tokens and all user data
    localStorage.removeItem('user_token');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('admin_user');
    setIsLoggedIn(false);
    setCustomerName('');
    setUnreadCount(0);
    setUserRoles([]);
    
    // Dispatch custom event to notify all components of logout
    window.dispatchEvent(new Event('loginStateChanged'));
    
    navigate('/travel');
  };

  return (
    <>
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-[1760px] mx-auto px-4 sm:px-10 lg:px-20">
          <div className="flex items-center justify-between h-16 md:h-20 gap-2">
            {/* Logo */}
            <div
              onClick={() => {
                // Force navigation with state to trigger reset
                navigate('/travel', { replace: true, state: { reset: true } });
              }}
              className="flex items-center cursor-pointer group"
            >
              {brand?.key === 'marketplace' ? (
                <UCWonderLogo
                  className="h-6 sm:h-7"
                  textClass="text-lg sm:text-xl"
                  data-testid="header-wordmark"
                />
              ) : (
                <>
                  <svg
                    className="w-8 h-8 transition-colors"
                    style={{ color: '#1F5C42' }}
                    viewBox="0 0 32 32"
                    fill="currentColor"
                  >
                    <path d="M16 1c2 0 3.46.96 4.75 3.27l.53 1.02a424.58 424.58 0 007.1 13.18 4 4 0 01-6.9 4.01l-.44-.76C19.5 19.5 18 16.84 16 16c-2 .84-3.5 3.5-5.04 5.72l-.44.76a4 4 0 01-6.9-4.01 424.58 424.58 0 007.1-13.18l.53-1.02C12.54 1.96 14 1 16 1z" />
                  </svg>
                  <span
                    className="ml-2 text-xl font-bold transition-colors hidden sm:block"
                    style={{ color: '#1E3F70' }}
                    data-testid="header-wordmark"
                  >
                    {brand?.displayName || 'Homesbyli'}
                  </span>
                </>
              )}
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-2 md:gap-4">
              {/* Conditional: Experience Montreal OR Find a Stay
                  Hidden entirely while the Experience Montreal page is
                  still being built (FEATURE_FLAGS.experienceMontreal).   */}
              {FEATURE_FLAGS.experienceMontreal && (
                location.pathname === '/experience-montreal' ? (
                  <Button
                    onClick={() => navigate('/search')}
                    variant="ghost"
                    className="hidden md:flex text-sm font-medium hover:bg-blue-50 text-blue-600 hover:text-blue-700 rounded-full px-4 border border-blue-200"
                  >
                    🏠 {t('nav.findAStay')}
                  </Button>
                ) : (
                  <Button
                    onClick={() => navigate('/experience-montreal')}
                    variant="ghost"
                    className="hidden md:flex text-sm font-medium hover:bg-rose-50 text-rose-600 hover:text-rose-700 rounded-full px-4 border border-rose-200"
                  >
                    ✨ {t('nav.experienceMontreal')}
                  </Button>
                )
              )}

              {/* Marketplace primary nav — AI concierge leads on the
                  UCWonder brand (per 2026-07-02 reposition around the
                  AI travel concierge narrative). Gift cards is still
                  reachable via the header icon further right + the
                  primaryNav footer, but it's no longer the top-level
                  hero CTA on ucwonder.ai. */}
              {brandKey === 'marketplace' && (
                <Button
                  onClick={() => navigate('/plan')}
                  variant="ghost"
                  className="hidden md:flex text-sm font-medium rounded-full px-4 gap-1.5"
                  style={{ color: '#1E3F70' }}
                  data-testid="header-concierge-nav"
                >
                  <Wand2 className="w-4 h-4" strokeWidth={1.8} />
                  UCWonder AI
                </Button>
              )}

              {/* Plan your trip — AI Concierge (now BEFORE Experiences) */}
              <Button
                onClick={() => navigate('/plan')}
                variant="ghost"
                className="hidden md:flex text-sm font-medium rounded-full px-4 gap-1.5"
                style={{ color: '#1E3F70' }}
                data-testid="header-plan-trip"
              >
                <Wand2 className="w-4 h-4" strokeWidth={1.8} />
                Plan your trip
              </Button>

              {/* Experiences (public approved partners) — moved AFTER Plan your trip */}
              <Button
                onClick={() => navigate('/experiences')}
                variant="ghost"
                className="hidden md:flex text-sm font-medium rounded-full px-4 gap-1.5"
                style={{ color: '#e68faa' }}
                data-testid="header-experiences"
              >
                <MapPin className="w-4 h-4" strokeWidth={1.8} />
                Experiences
              </Button>

              {/* Become a Partner (offer experiences) —
                  Hidden on marketplace brand since partner onboarding
                  lives under the stays brand for now. */}
              {brandKey !== 'marketplace' && (
                <Button
                  onClick={() => navigate('/become-a-partner')}
                  variant="ghost"
                  className="hidden md:flex text-sm font-medium hover:bg-gray-100 rounded-full px-4"
                  data-testid="header-become-partner"
                >
                  Become a Partner
                </Button>
              )}

              {/* Language Selector — hidden on UCWonder (clean new-design header) */}
              {brandKey !== 'marketplace' && <LanguageSwitcher variant="icon" />}

              {/* Notification Bell — hidden on UCWonder (clean header) */}
              {isLoggedIn && brandKey !== 'marketplace' && <NotificationBell />}

              {/* AI Assistant Button — hidden on UCWonder (the AI concierge nav link covers it) */}
              {brandKey !== 'marketplace' && (
                <Button
                  onClick={() => setShowAIChat(true)}
                  variant="ghost"
                  size="icon"
                  className="rounded-full hover:bg-gray-100 relative"
                  title={t('nav.aiAssistant')}
                >
                  <Sparkles className="w-5 h-5 text-rose-600" />
                  <span className="absolute -top-0.5 -right-0.5 bg-blue-500 text-white text-[10px] rounded-full w-3.5 h-3.5 flex items-center justify-center font-bold">
                    AI
                  </span>
                </Button>
              )}

              {/* Wallet icon — only visible when logged in. Shows a badge
                  with the count of active vouchers so a customer who bought
                  a card weeks ago always has a path back to redeem it.
                  Hidden on the welcome/landing page ("/") per product decision
                  — the promo strip on that page already surfaces the wallet
                  and we don't want a second entry point competing for attention. */}
              {isLoggedIn && brandKey !== 'marketplace' && location.pathname !== '/' && (
                <Button
                  onClick={() => navigate('/wallet')}
                  variant="ghost"
                  size="icon"
                  className="rounded-full hover:bg-gray-100 relative"
                  title="My Wallet"
                  data-testid="header-wallet-btn"
                >
                  <Wallet className="w-5 h-5" style={{ color: '#1F5C42' }} />
                  {walletCount > 0 && (
                    <span
                      className="absolute -top-0.5 -right-0.5 text-white text-[10px] rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center font-bold"
                      style={{ background: '#FF5C8A' }}
                      data-testid="header-wallet-badge"
                    >
                      {walletCount > 9 ? '9+' : walletCount}
                    </span>
                  )}
                </Button>
              )}

              {/* QR Code Button — hidden on UCWonder (clean new-design header) */}
              {brandKey !== 'marketplace' && (
                <Button
                  onClick={() => setShowQRModal(true)}
                  variant="ghost"
                  size="icon"
                  className="hidden md:inline-flex rounded-full hover:bg-gray-100"
                  title={t('nav.showWebsiteQr')}
                >
                  <QrCode className="w-5 h-5" />
                </Button>
              )}

              {/* Role Switcher - For users with multiple roles (hidden on UCWonder) */}
              {isLoggedIn && brandKey !== 'marketplace' && <RoleSwitcher />}

              {/* User Menu — UCWonder gets one clean account button instead */}
              {brandKey === 'marketplace' ? (
                <Button
                  onClick={() => navigate(isLoggedIn ? '/dashboard' : '/login')}
                  className="rounded-full px-5 text-white font-semibold hover:opacity-90"
                  style={{ backgroundColor: '#1E3F70' }}
                  data-testid="ucw-header-account-btn"
                >
                  <User className="w-4 h-4 mr-1.5" />
                  {isLoggedIn ? 'My trips' : 'Sign in'}
                </Button>
              ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="flex items-center gap-2 rounded-full border-gray-300 hover:shadow-md transition-shadow pl-3 pr-2 py-2 h-auto relative z-50"
                  >
                    <Menu className="w-4 h-4" />
                    <div className="w-8 h-8 bg-gray-500 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-white" />
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 mt-2 relative z-50">
                  {isLoggedIn ? (
                    <>
                      <div className="px-2 py-2 border-b">
                        <p className="text-sm font-medium text-gray-900">{customerName}</p>
                        <p className="text-xs text-gray-500">
                          {t('nav.customerAccount')}
                        </p>
                      </div>
                      <DropdownMenuItem
                        onClick={() => navigate('/dashboard')}
                        className="cursor-pointer"
                      >
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        {t('nav.dashboard')}
                      </DropdownMenuItem>
                      {userRoles.includes('partner') && !location.pathname.includes('/partner/dashboard') && (
                        <DropdownMenuItem
                          onClick={() => navigate('/partner/dashboard')}
                          className="cursor-pointer"
                          data-testid="header-menu-partner-dashboard"
                        >
                          <Sparkles className="w-4 h-4 mr-2" style={{ color: '#1F5C42' }} />
                          <span className="font-medium">Partner Dashboard</span>
                        </DropdownMenuItem>
                      )}
                      {userRoles.includes('admin') && !location.pathname.includes('/admin/dashboard') && (
                        <DropdownMenuItem
                          onClick={() => navigate('/admin/dashboard')}
                          className="cursor-pointer bg-rose-50 hover:bg-rose-100"
                        >
                          <Building2 className="w-4 h-4 mr-2 text-rose-600" />
                          <span className="font-semibold text-rose-600">{t('nav.adminDashboard')}</span>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => navigate('/messages')}
                        className="cursor-pointer flex items-center justify-between"
                      >
                        <div className="flex items-center">
                          <MessageCircle className="w-4 h-4 mr-2" />
                          {t('nav.messages')}
                        </div>
                        {unreadCount > 0 && (
                          <span className="bg-rose-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                            {unreadCount}
                          </span>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => navigate('/wishlists')}
                        className="cursor-pointer"
                        data-testid="user-menu-wishlists"
                      >
                        <Heart className="w-4 h-4 mr-2" />
                        {t('nav.wishlists')}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => navigate('/wallet')}
                        className="cursor-pointer flex items-center justify-between"
                        data-testid="user-menu-wallet"
                      >
                        <div className="flex items-center">
                          <Wallet className="w-4 h-4 mr-2" style={{ color: '#1F5C42' }} />
                          My Wallet
                        </div>
                        {walletCount > 0 && (
                          <span className="text-white text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: '#FF5C8A' }}>
                            {walletCount}
                          </span>
                        )}
                      </DropdownMenuItem>
                      {/* Gift cards dropdown item — visible on marketplace
                          brand only (Stays users find gift cards via the
                          UCWonder cross-brand link). */}
                      {brandKey === 'marketplace' && (
                        <DropdownMenuItem
                          onClick={() => navigate('/dining')}
                          className="cursor-pointer"
                          data-testid="user-menu-giftcards"
                        >
                          <Gift className="w-4 h-4 mr-2" style={{ color: '#FF5C8A' }} />
                          Gift cards
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => navigate('/booking-lookup')}
                        className="cursor-pointer"
                      >
                        {t('nav.bookingLookup')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {FEATURE_FLAGS.experienceMontreal && (
                        location.pathname === '/experience-montreal' ? (
                          <DropdownMenuItem
                            onClick={() => navigate('/search')}
                            className="cursor-pointer text-blue-600 font-medium"
                          >
                            🏠 {t('nav.findAStay')}
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() => navigate('/experience-montreal')}
                            className="cursor-pointer text-rose-600 font-medium"
                          >
                            ✨ {t('nav.experienceMontreal')}
                          </DropdownMenuItem>
                        )
                      )}
                      <DropdownMenuItem
                        onClick={() => navigate('/plan')}
                        className="cursor-pointer font-medium gap-1.5"
                        style={{ color: '#1E3F70' }}
                        data-testid="header-menu-plan-trip-loggedin"
                      >
                        <Wand2 className="w-4 h-4" strokeWidth={1.8} />
                        Plan your trip
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => navigate('/become-a-partner')}
                        className="cursor-pointer"
                        data-testid="header-menu-become-partner-loggedin"
                      >
                        Become a Partner
                      </DropdownMenuItem>
                      <DropdownMenuItem className="cursor-pointer">{t('nav.helpCenter')}</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={handleLogout}
                        className="cursor-pointer text-red-600"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        {t('nav.logout')}
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <>
                      <DropdownMenuItem
                        onClick={() => navigate('/signup')}
                        className="font-medium cursor-pointer"
                      >
                        {t('nav.signup')}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => navigate('/login')}
                        className="cursor-pointer"
                      >
                        {t('nav.login')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => navigate('/booking-lookup')}
                        className="cursor-pointer"
                      >
                        {t('nav.bookingLookup')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {FEATURE_FLAGS.experienceMontreal && (
                        location.pathname === '/experience-montreal' ? (
                          <DropdownMenuItem
                            onClick={() => navigate('/search')}
                            className="cursor-pointer text-blue-600 font-medium"
                          >
                            🏠 {t('nav.findAStay')}
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() => navigate('/experience-montreal')}
                            className="cursor-pointer text-rose-600 font-medium"
                          >
                            ✨ {t('nav.experienceMontreal')}
                          </DropdownMenuItem>
                        )
                      )}
                      <DropdownMenuItem
                        onClick={() => navigate('/plan')}
                        className="cursor-pointer font-medium gap-1.5"
                        style={{ color: '#1E3F70' }}
                        data-testid="header-menu-plan-trip"
                      >
                        <Wand2 className="w-4 h-4" strokeWidth={1.8} />
                        Plan your trip
                      </DropdownMenuItem>
                      {brandKey === 'marketplace' && (
                        <DropdownMenuItem
                          onClick={() => navigate('/dining')}
                          className="cursor-pointer"
                          data-testid="header-menu-giftcards-loggedout"
                        >
                          <Gift className="w-4 h-4 mr-2" style={{ color: '#FF5C8A' }} />
                          Gift cards
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem 
                        onClick={() => navigate('/become-a-partner')}
                        className="cursor-pointer"
                        data-testid="header-menu-become-partner"
                      >
                        Become a Partner
                      </DropdownMenuItem>
                      <DropdownMenuItem className="cursor-pointer">{t('nav.helpCenter')}</DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Auth Modal */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        mode={authMode}
        onModeChange={setAuthMode}
      />

      {/* Website QR Modal */}
      <WebsiteQRModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
      />

      {/* AI Chat Assistant */}
      <AIChatAssistant
        isOpen={showAIChat}
        onClose={() => setShowAIChat(false)}
      />
    </>
  );
};

// UCWonder brand always gets the shared new nav (identical on every page);
// the Homesbyli host brand keeps its full legacy header.
const Header = () => {
  const { brandKey } = useBrand();
  return brandKey === 'marketplace' ? <UCWonderNav /> : <LegacyHeader />;
};

export default Header;
